import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  PropertyAttributesDisplay,
  PropertyAttributesDisplayTranslations,
  AnalysisSectionHeader,
  Text,
  Stack,
  Loading,
  ImageGallery,
  media,
} from '@popety-io/ui'
import type { PropertyAttributes } from '@popety-io/types'
import { useClearMapSection } from '../hooks'

// API base URL for constructing public file URLs
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.large};

  ${media.mobileOnly} {
    padding: ${({ theme }) => theme.spacing.medium};
  }
`

const ContentCard = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.large};
  margin-bottom: ${({ theme }) => theme.spacing.large};

  ${media.mobileOnly} {
    padding: ${({ theme }) => theme.spacing.medium};
    margin-bottom: ${({ theme }) => theme.spacing.medium};
  }
`

const DescriptionSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

const DescriptionText = styled.div`
  white-space: pre-wrap;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};

  p {
    margin: 0 0 ${({ theme }) => theme.spacing.small} 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

/** Sanitize HTML to only allow safe formatting tags */
const sanitizeHtml = (html: string): string => {
  const allowed = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li']
  return html.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, (match, tag) => {
    return allowed.includes(tag.toLowerCase()) ? match : ''
  })
}

const SituationSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  min-height: 400px;

  ${media.mobileOnly} {
    padding: ${({ theme }) => theme.spacing.medium};
    min-height: 200px;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xlarge};
  color: ${({ theme }) => theme.colors.text.secondary};
`

const PhotosSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

const PhotosGalleryContainer = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
`

interface PropertyPhoto {
  fileId: string
  url?: string
  order: number
  name?: string
}

interface PropertyPhotosData {
  photos: PropertyPhoto[]
}

interface PropertyDetailsTabProps {
  estimateData: any
  hash?: string
}

export const PropertyDetailsTab: React.FC<PropertyDetailsTabProps> = ({
  estimateData,
  hash,
}) => {
  const { t } = useTranslation('estimate')
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)

  // Clear map section data when viewing property details
  useClearMapSection()

  // Get property photos from estimate data
  const propertyPhotos = useMemo((): PropertyPhotosData | null => {
    const photos = estimateData?.value_edited?.property_photos
    if (!photos || !photos.photos || photos.photos.length === 0) {
      return null
    }
    return photos
  }, [estimateData])

  // Build photo URLs using the public endpoint
  const photoUrls = useMemo(() => {
    if (!propertyPhotos || !hash) return []

    // Sort photos by order and build URLs
    const sortedPhotos = [...propertyPhotos.photos].sort(
      (a, b) => a.order - b.order
    )

    return sortedPhotos.map(
      (photo) =>
        `${API_BASE_URL}/estimate-public-shares/public/${hash}/files/${photo.fileId}`
    )
  }, [propertyPhotos, hash])

  // Get property description from estimate data
  const description = useMemo(() => {
    return (
      estimateData?.property_description ||
      estimateData?.value_edited?.property_description ||
      ''
    )
  }, [estimateData])

  // Get property attributes from estimate data
  const attributes: PropertyAttributes = useMemo(() => {
    return (
      estimateData?.property_attributes ||
      estimateData?.value_edited?.property_attributes ||
      {}
    )
  }, [estimateData])

  // Get situation/location description
  const situationLocation = useMemo(() => {
    return estimateData?.value_edited?.situation_location || ''
  }, [estimateData])

  // Build translations for PropertyAttributesDisplay
  const attributesDisplayTranslations =
    useMemo((): PropertyAttributesDisplayTranslations => {
      return {
        categories: {
          overview: t('propertyAttributes.categories.overview'),
          landBuilding: t('propertyAttributes.categories.landBuilding'),
          energy: t('propertyAttributes.categories.energy'),
          exterior: t('propertyAttributes.categories.exterior'),
          interior: t('propertyAttributes.categories.interior'),
          environment: t('propertyAttributes.categories.environment'),
        },
        fields: {
          propertyType: t('propertyAttributes.fields.propertyType'),
          propertySubtype: t('propertyAttributes.fields.propertySubtype'),
          livingArea: t('propertyAttributes.fields.livingArea'),
          constructionYear: t('propertyAttributes.fields.constructionYear'),
          roomsNb: t('propertyAttributes.fields.roomsNb'),
          numberOfBathrooms: t('propertyAttributes.fields.numberOfBathrooms'),
          floor: t('propertyAttributes.fields.floor'),
          landArea: t('propertyAttributes.fields.landArea'),
          usableArea: t('propertyAttributes.fields.usableArea'),
          weightedArea: t('propertyAttributes.fields.weightedArea'),
          volume: t('propertyAttributes.fields.volume'),
          buildingHeight: t('propertyAttributes.fields.buildingHeight'),
          numberOfFloors: t('propertyAttributes.fields.numberOfFloors'),
          floorArea: t('propertyAttributes.fields.floorArea'),
          heatingType: t('propertyAttributes.fields.heatingType'),
          minergie: t('propertyAttributes.fields.minergie'),
          solarPotential: t('propertyAttributes.fields.solarPotential'),
          energyClass: t('propertyAttributes.fields.energyClass'),
          solarPanelsInstalled: t(
            'propertyAttributes.fields.solarPanelsInstalled'
          ),
          solarArea: t('propertyAttributes.fields.solarArea'),
          heatConsumptionIndex: t(
            'propertyAttributes.fields.heatConsumptionIndex'
          ),
          parking: t('propertyAttributes.fields.parking'),
          garage: t('propertyAttributes.fields.garage'),
          hasBalcony: t('propertyAttributes.fields.hasBalcony'),
          balconyArea: t('propertyAttributes.fields.balconyArea'),
          hasTerrace: t('propertyAttributes.fields.hasTerrace'),
          terraceArea: t('propertyAttributes.fields.terraceArea'),
          elevator: t('propertyAttributes.fields.elevator'),
          cellar: t('propertyAttributes.fields.cellar'),
          attic: t('propertyAttributes.fields.attic'),
          hasGarden: t('propertyAttributes.fields.hasGarden'),
          gardenArea: t('propertyAttributes.fields.gardenArea'),
          pool: t('propertyAttributes.fields.pool'),
          sauna: t('propertyAttributes.fields.sauna'),
          fireplace: t('propertyAttributes.fields.fireplace'),
          airConditioning: t('propertyAttributes.fields.airConditioning'),
          condition: t('propertyAttributes.fields.condition'),
          renovationYear: t('propertyAttributes.fields.renovationYear'),
          lakeView: t('propertyAttributes.fields.lakeView'),
          mountainView: t('propertyAttributes.fields.mountainView'),
          noiseLevel: t('propertyAttributes.fields.noiseLevel'),
          publicTransportQuality: t(
            'propertyAttributes.fields.publicTransportQuality'
          ),
          exposureOrientation: t(
            'propertyAttributes.fields.exposureOrientation'
          ),
        },
        units: {
          squareMeters: t('propertyAttributes.units.squareMeters'),
          cubicMeters: t('propertyAttributes.units.cubicMeters'),
          meters: t('propertyAttributes.units.meters'),
          years: t('propertyAttributes.units.years'),
          rooms: t('propertyAttributes.units.rooms'),
          spaces: t('propertyAttributes.units.spaces'),
        },
        boolean: {
          yes: t('propertyAttributes.boolean.yes'),
          no: t('propertyAttributes.boolean.no'),
        },
        emptyState: t('propertyAttributes.emptyState'),
        options: {
          heatingType: {
            oil: t('propertyAttributes.options.heatingType.oil'),
            gas: t('propertyAttributes.options.heatingType.gas'),
            electricity: t(
              'propertyAttributes.options.heatingType.electricity'
            ),
            heat_pump: t('propertyAttributes.options.heatingType.heat_pump'),
            wood: t('propertyAttributes.options.heatingType.wood'),
            pellets: t('propertyAttributes.options.heatingType.pellets'),
            district_heating: t(
              'propertyAttributes.options.heatingType.district_heating'
            ),
            solar_thermal: t(
              'propertyAttributes.options.heatingType.solar_thermal'
            ),
            other: t('propertyAttributes.options.heatingType.other'),
          },
          quality: {
            bad: t('propertyAttributes.options.quality.bad'),
            medium: t('propertyAttributes.options.quality.medium'),
            good: t('propertyAttributes.options.quality.good'),
            excellent: t('propertyAttributes.options.quality.excellent'),
          },
          view: {
            none: t('propertyAttributes.options.view.none'),
            partial: t('propertyAttributes.options.view.partial'),
            good: t('propertyAttributes.options.view.good'),
            excellent: t('propertyAttributes.options.view.excellent'),
          },
          noise: {
            low: t('propertyAttributes.options.noise.low'),
            medium: t('propertyAttributes.options.noise.medium'),
            high: t('propertyAttributes.options.noise.high'),
          },
          energyClass: {
            A: t('propertyAttributes.options.energyClass.A'),
            B: t('propertyAttributes.options.energyClass.B'),
            C: t('propertyAttributes.options.energyClass.C'),
            D: t('propertyAttributes.options.energyClass.D'),
            E: t('propertyAttributes.options.energyClass.E'),
            F: t('propertyAttributes.options.energyClass.F'),
            G: t('propertyAttributes.options.energyClass.G'),
          },
          propertyType: {
            apartment: t('propertyAttributes.options.propertyType.apartment'),
            house: t('propertyAttributes.options.propertyType.house'),
          },
          apartmentSubtype: {
            studio: t('propertyAttributes.options.apartmentSubtype.studio'),
            ground_floor_garden: t(
              'propertyAttributes.options.apartmentSubtype.ground_floor_garden'
            ),
            ground_floor: t(
              'propertyAttributes.options.apartmentSubtype.ground_floor'
            ),
            floor: t('propertyAttributes.options.apartmentSubtype.floor'),
            duplex: t('propertyAttributes.options.apartmentSubtype.duplex'),
            attic: t('propertyAttributes.options.apartmentSubtype.attic'),
            penthouse: t(
              'propertyAttributes.options.apartmentSubtype.penthouse'
            ),
          },
          houseSubtype: {
            detached: t('propertyAttributes.options.houseSubtype.detached'),
            semi_detached: t(
              'propertyAttributes.options.houseSubtype.semi_detached'
            ),
            terraced: t('propertyAttributes.options.houseSubtype.terraced'),
            farmhouse: t('propertyAttributes.options.houseSubtype.farmhouse'),
          },
          condition: {
            new: t('propertyAttributes.options.condition.new'),
            good: t('propertyAttributes.options.condition.good'),
            fair: t('propertyAttributes.options.condition.fair'),
            needs_renovation: t(
              'propertyAttributes.options.condition.needs_renovation'
            ),
          },
          orientation: {
            north: t('propertyAttributes.options.orientation.north'),
            north_east: t('propertyAttributes.options.orientation.north_east'),
            east: t('propertyAttributes.options.orientation.east'),
            south_east: t('propertyAttributes.options.orientation.south_east'),
            south: t('propertyAttributes.options.orientation.south'),
            south_west: t('propertyAttributes.options.orientation.south_west'),
            west: t('propertyAttributes.options.orientation.west'),
            north_west: t('propertyAttributes.options.orientation.north_west'),
          },
        },
      }
    }, [t])

  // Loading state
  if (!estimateData) {
    return (
      <Container>
        <LoadingWrapper>
          <Loading size="large" />
        </LoadingWrapper>
      </Container>
    )
  }

  const hasContent =
    description ||
    situationLocation ||
    Object.keys(attributes).length > 0 ||
    photoUrls.length > 0

  // Empty state
  if (!hasContent) {
    return (
      <Container>
        <AnalysisSectionHeader
          title={t('propertyDetails.title')}
          subtitle={t('propertyDetails.subtitle')}
        />
        <EmptyState>
          <Text variant="body1" color="secondary">
            {t('propertyDetails.noDataAvailable')}
          </Text>
        </EmptyState>
      </Container>
    )
  }

  return (
    <Container>
      <AnalysisSectionHeader
        title={t('propertyDetails.title')}
        subtitle={t('propertyDetails.subtitle')}
      />

      {/* Property Photos */}
      {photoUrls.length > 0 && (
        <ContentCard>
          <PhotosSection>
            <Stack spacing="medium">
              <Text variant="h5" weight="semibold">
                {t('propertyDetails.photos')}
              </Text>
              <PhotosGalleryContainer>
                <ImageGallery
                  images={photoUrls}
                  currentIndex={currentPhotoIndex}
                  onIndexChange={setCurrentPhotoIndex}
                  showThumbnails
                  showControls
                  showCounter
                  height="clamp(250px, 50vw, 400px)"
                />
              </PhotosGalleryContainer>
            </Stack>
          </PhotosSection>
        </ContentCard>
      )}

      {/* Property Description */}
      {description && (
        <ContentCard>
          <DescriptionSection>
            <Stack spacing="medium">
              <Text variant="h5" weight="semibold">
                {t('propertyDetails.description')}
              </Text>
              <DescriptionText
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(description),
                }}
              />
            </Stack>
          </DescriptionSection>
        </ContentCard>
      )}

      {/* Situation & Location */}
      {situationLocation && (
        <ContentCard>
          <SituationSection>
            <Stack spacing="medium">
              <Text variant="h5" weight="semibold">
                {t('propertyDetails.situationLocation')}
              </Text>
              <DescriptionText
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(situationLocation),
                }}
              />
            </Stack>
          </SituationSection>
        </ContentCard>
      )}

      {/* Property Attributes (Read-Only) */}
      {Object.keys(attributes).length > 0 && (
        <ContentCard>
          <Stack spacing="medium">
            <Text variant="h5" weight="semibold">
              {t('propertyDetails.attributes')}
            </Text>
            <PropertyAttributesDisplay
              attributes={attributes}
              translations={attributesDisplayTranslations}
            />
          </Stack>
        </ContentCard>
      )}
    </Container>
  )
}
